import { useMemo, useState } from "react";
import {
  SecretField, Button, Card, Field } from "@leylion/design-system";
import { BRAND } from "../brand";
import {
  requestPasswordRecovery,
  resetRecoveredPassword
} from "./password-recovery-client";
import "./password-recovery.css";

type Language = "ar" | "en";
type Mode = "request" | "reset";

function readToken(): string {
  const hash = window.location.hash;
  const queryIndex = hash.indexOf("?");
  if (queryIndex < 0) return "";
  return new URLSearchParams(hash.slice(queryIndex + 1)).get("token") ?? "";
}

function messageOf(error: unknown, language: Language): string {
  const problem = error as Error & { problem?: { title?: string; detail?: string } };
  const code = problem.problem?.title ?? problem.message;
  if (code === "PASSWORD_POLICY_FAILED") {
    return language === "ar"
      ? "استخدم 10 خانات على الأقل، مع حرف كبير وصغير ورقم ورمز."
      : "Use at least 10 characters with upper and lower case letters, a number and a symbol.";
  }
  if (code === "PASSWORD_REUSE_FORBIDDEN") {
    return language === "ar"
      ? "اختر كلمة مرور مختلفة عن كلمة المرور السابقة."
      : "Choose a password different from the previous password.";
  }
  if (code === "PASSWORD_RECOVERY_TOKEN_INVALID") {
    return language === "ar"
      ? "رابط الاستعادة غير صالح أو انتهت صلاحيته. اطلب رابطًا جديدًا."
      : "This recovery link is invalid or expired. Request a new link.";
  }
  return problem.problem?.detail || problem.message || (
    language === "ar" ? "تعذر إكمال الطلب." : "The request could not be completed."
  );
}

export function PasswordRecoveryExperience({
  mode,
  onSignIn,
  onRequestNew
}: {
  mode: Mode;
  onSignIn: () => void;
  onRequestNew: () => void;
}) {
  const [language, setLanguage] = useState<Language>(() =>
    localStorage.getItem("leylion.language") === "en" ? "en" : "ar"
  );
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [completed, setCompleted] = useState(false);
  const token = useMemo(readToken, []);
  const isArabic = language === "ar";

  const submitRequest = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await requestPasswordRecovery(email);
      setCompleted(true);
    } catch {
      // Account recovery never changes its public response based on whether an
      // address exists. Preserve the same safe completion UX for provider noise.
      setCompleted(true);
    } finally {
      setBusy(false);
    }
  };

  const submitReset = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!token) {
      setError(isArabic ? "رابط الاستعادة غير مكتمل." : "The recovery link is incomplete.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(isArabic ? "كلمتا المرور غير متطابقتين." : "Passwords do not match.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await resetRecoveredPassword(token, newPassword);
      setNewPassword("");
      setConfirmPassword("");
      setCompleted(true);
    } catch (caught) {
      setError(messageOf(caught, language));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="password-recovery-page" dir={isArabic ? "rtl" : "ltr"}>
      <Card className="password-recovery-card">
        <div className="password-recovery-header">
          <img src={BRAND.assets.wordmarkDark} alt={BRAND.productName} />
          <button
            type="button"
            onClick={() => {
              const next = isArabic ? "en" : "ar";
              localStorage.setItem("leylion.language", next);
              setLanguage(next);
            }}
          >
            {isArabic ? "EN" : "AR"}
          </button>
        </div>

        {mode === "request" ? (
          <>
            <h1>{isArabic ? "استعادة كلمة المرور" : "Recover your password"}</h1>
            <p>
              {isArabic
                ? "أدخل بريد حسابك. إذا كان الحساب فعالًا سنرسل إليه رابطًا آمنًا قصير الصلاحية."
                : "Enter your account email. If the account is active, we’ll send a short-lived secure reset link."}
            </p>
            {completed ? (
              <div className="password-recovery-success" role="status">
                <strong>{isArabic ? "تحقق من بريدك الإلكتروني" : "Check your email"}</strong>
                <span>
                  {isArabic
                    ? "إذا كان البريد مرتبطًا بحساب Leylion فعال، فستصلك تعليمات إعادة التعيين."
                    : "If that address belongs to an active Leylion account, reset instructions will arrive shortly."}
                </span>
              </div>
            ) : (
              <form onSubmit={submitRequest}>
                <Field
                  data-testid="password-recovery-email"
                  label={isArabic ? "البريد الإلكتروني" : "Email"}
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                />
                <Button data-testid="password-recovery-submit" disabled={busy} type="submit">
                  {busy ? "…" : isArabic ? "إرسال رابط الاستعادة" : "Send recovery link"}
                </Button>
              </form>
            )}
          </>
        ) : (
          <>
            <h1>{isArabic ? "تعيين كلمة مرور جديدة" : "Set a new password"}</h1>
            <p>
              {isArabic
                ? "بعد نجاح التغيير سيتم إبطال جلسات الدخول الحالية لحماية الحساب."
                : "After a successful reset, existing signed-in sessions are revoked to protect the account."}
            </p>
            {completed ? (
              <div className="password-recovery-success" role="status">
                <strong>{isArabic ? "تم تغيير كلمة المرور" : "Password changed"}</strong>
                <span>
                  {isArabic
                    ? "يمكنك الآن تسجيل الدخول بكلمة المرور الجديدة."
                    : "You can now sign in with your new password."}
                </span>
              </div>
            ) : (
              <form onSubmit={submitReset}>
                <SecretField
                  data-testid="password-recovery-new-password"
                  label={isArabic ? "كلمة المرور الجديدة" : "New password"}
                  revealLabel={isArabic ? "إظهار كلمة المرور الجديدة" : "Show new password"}
                  concealLabel={isArabic ? "إخفاء كلمة المرور الجديدة" : "Hide new password"}
                      autoComplete="new-password"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  required
                />
                <SecretField
                  data-testid="password-recovery-confirm-password"
                  label={isArabic ? "تأكيد كلمة المرور" : "Confirm password"}
                  revealLabel={isArabic ? "إظهار تأكيد كلمة المرور" : "Show password confirmation"}
                  concealLabel={isArabic ? "إخفاء تأكيد كلمة المرور" : "Hide password confirmation"}
                      autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  required
                />
                {error ? <div className="password-recovery-error" role="alert">{error}</div> : null}
                <Button data-testid="password-recovery-reset" disabled={busy} type="submit">
                  {busy ? "…" : isArabic ? "حفظ كلمة المرور" : "Save password"}
                </Button>
              </form>
            )}
          </>
        )}

        <div className="password-recovery-actions">
          {mode === "reset" && !completed ? (
            <button type="button" onClick={onRequestNew}>
              {isArabic ? "طلب رابط جديد" : "Request a new link"}
            </button>
          ) : null}
          <button type="button" onClick={onSignIn}>
            {isArabic ? "العودة لتسجيل الدخول" : "Back to sign in"}
          </button>
        </div>
      </Card>
    </div>
  );
}
